import React, { useState, createElement } from 'react';
import { User, Briefcase, MapPin, CheckCircle, ChevronRight, ArrowLeft, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
export const LoungeOnboard = () => {
  const [step, setStep] = useState(1);
  const totalSteps = 4;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    type: '',
    fullName: '',
    email: '',
    phone: '',
    brandName: '',
    expertise: '',
    cityId: '',
    cityName: ''
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const nextStep = () => {
    if (step < totalSteps) setStep(step + 1);
  };
  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };
  const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzR_-KmqKZFGGbKzHfTDJ1Shbo07C0RSOwx4RKTH9sU4PjA-r7elviN739j-03EfNIa/exec';
  // Submit via image beacon GET request — works in ALL environments
  // (sandboxed iframes, CSP restrictions, cross-origin, redirects)
  const submitToSheet = (data: Record<string, string>): Promise<void> => {
    return new Promise((resolve, reject) => {
      const params = new URLSearchParams(data);
      const url = `${GOOGLE_SCRIPT_URL}?${params.toString()}`;
      // Method 1: Image beacon (most reliable, works everywhere)
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => {
        // Image onerror fires even on successful requests (since response isn't an image)
        // So we treat this as success too
        resolve();
      };
      img.src = url;
      // Safety timeout — resolve after 3s regardless
      setTimeout(() => resolve(), 3000);
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step < totalSteps) {
      nextStep();
    } else {
      // Submit to Google Sheets via image beacon GET request
      setIsSubmitting(true);
      setSubmitError('');
      try {
        await submitToSheet({
          Timestamp: new Date().toISOString(),
          Type: formData.type,
          'Full Name': formData.fullName,
          Email: formData.email,
          Phone: formData.phone,
          'Brand Name': formData.brandName,
          Expertise: formData.expertise,
          'City ID': formData.cityId,
          'City Name': formData.cityName
        });
        setIsSubmitted(true);
      } catch (error) {
        console.error('Submission error:', error);
        setSubmitError('Failed to submit. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  // Custom styles for this specific page to match the reference
  const pageStyles = `
    .lounge-bg {
      background-color: #111827; /* Dark charcoal/navy */
      background-image: 
        linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
      background-size: 40px 40px;
      color: #f3f4f6;
      min-height: 100vh;
      font-family: 'Inter', sans-serif;
    }
    
    .lounge-card {
      background-color: #1f2937;
      border: 1px solid rgba(239, 68, 68, 0.2); /* Subtle red border */
      box-shadow: 0 0 20px rgba(239, 68, 68, 0.1), inset 0 0 20px rgba(239, 68, 68, 0.05); /* Red glow */
      border-radius: 12px;
    }
    
    .lounge-label {
      color: #ef4444; /* Red accent */
      font-family: 'IBM Plex Mono', 'Courier New', monospace;
      font-size: 0.75rem;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      margin-bottom: 0.5rem;
      display: block;
    }
    
    .lounge-input {
      background-color: #111827;
      border: 1px solid #374151;
      color: #f3f4f6;
      border-radius: 6px;
      padding: 0.75rem 1rem;
      width: 100%;
      font-family: 'IBM Plex Mono', 'Courier New', monospace;
      transition: all 0.2s ease;
    }
    
    .lounge-input:focus {
      outline: none;
      border-color: #ef4444;
      box-shadow: 0 0 0 1px rgba(239, 68, 68, 0.5);
    }
    
    .lounge-input::placeholder {
      color: #4b5563;
    }
    
    .lounge-btn {
      background-color: #4b5563; /* Grayed out initially */
      color: #d1d5db;
      font-family: 'IBM Plex Mono', 'Courier New', monospace;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      border: none;
      border-radius: 6px;
      padding: 1rem;
      width: 100%;
      font-weight: 600;
      transition: all 0.3s ease;
      cursor: pointer;
    }
    
    .lounge-btn:hover {
      background-color: #6b7280;
      color: #ffffff;
    }
    
    .lounge-btn.active {
      background-color: #ef4444;
      color: #ffffff;
    }
    
    .lounge-btn.active:hover {
      background-color: #dc2626;
      box-shadow: 0 0 15px rgba(239, 68, 68, 0.4);
    }
    
    .step-indicator {
      font-family: 'IBM Plex Mono', 'Courier New', monospace;
      color: #ef4444;
      font-size: 0.875rem;
      display: flex;
      align-items: center;
      gap: 1rem;
    }
    
    .progress-bar-container {
      height: 4px;
      background-color: #374151;
      border-radius: 2px;
      width: 60px;
      overflow: hidden;
    }
    
    .progress-bar-fill {
      height: 100%;
      background-color: #ef4444;
      box-shadow: 0 0 8px #ef4444;
      transition: width 0.3s ease;
    }
    
    .scanline {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 2px;
      background-color: rgba(239, 68, 68, 0.3);
      box-shadow: 0 0 10px rgba(239, 68, 68, 0.5);
      animation: scan 8s linear infinite;
      pointer-events: none;
      z-index: 50;
    }
    
    @keyframes scan {
      0% { top: -10%; }
      100% { top: 110%; }
    }
  `;
  // If submitted successfully, show success screen
  if (isSubmitted) {
    return <div className="lounge-bg flex flex-col items-center justify-center py-12 px-4 relative" data-id="element-3858" data-mp="3gakrc4c86ys5">
        <style data-id="element-3859" data-mp="3la764i7pcn32">{`{pageStyles}`}</style>
        <div className="scanline" data-id="element-3860" data-mp="3l95kxkwz2k7m"></div>
        <div className="w-full max-w-xl" data-id="element-3861" data-mp="3l84a9hjvbzqf">
          <div className="lounge-card p-8 md:p-10 text-center" data-id="element-3862" data-mp="3q1p7v8hjurp3">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 border border-green-500/40 flex items-center justify-center" data-id="element-3863" data-mp="3uzqntkjoiawc">
              <CheckCircle className="text-green-500" size={40} data-id="element-3864" data-mp="2umtxyd2qqkc" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-3865" data-mp="7urdxyd6y3fb">
              SEQUENCE INITIALIZED
            </h2>
            <p className="text-gray-400 font-mono text-sm mb-8" data-id="element-3866" data-mp="csswmi6pg0q9">
              Your telemetry data has been transmitted successfully.
              <br data-id="element-3867" data-mp="29oi6wcbf8n9f" />
              Welcome to The Lounge.
            </p>
            <div className="space-y-4" data-id="element-3868" data-mp="2em0kig0dvqnr">
              <a href="https://thepod.thenetworkingsalon.live/salon" className="lounge-btn active inline-flex justify-center items-center gap-2 w-full" data-id="element-3869" data-mp="271zgbe63ty9h">
                PROCEED TO PAYMENT
                <ChevronRight size={18} data-id="element-3870" data-mp="1xd7rhewcbu2s" />
              </a>
              <a href="https://chat.whatsapp.com/LAWDlIeeKSuFIUq1dwvZYe" target="_blank" rel="noopener noreferrer" className="lounge-btn inline-flex justify-center items-center gap-2 w-full" style={{
              backgroundColor: '#25D366',
              color: '#ffffff'
            }} data-id="element-3871" data-mp="1sd39tljp4taz">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-3872" data-mp="2b3kzfh98f8s4">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-3873" data-mp="14oa8f9kb1t8z" />
                </svg>
                JOIN WHATSAPP FREE LOUNGE
              </a>
              <Link to="/" className="lounge-btn inline-flex justify-center items-center gap-2 w-full" data-id="element-3874" data-mp="kbtryg7cqo2j">
                <ArrowLeft size={18} data-id="element-3875" data-mp="u3zgffyeancz" />
                RETURN TO BASE
              </Link>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="lounge-bg flex flex-col items-center justify-center py-12 px-4 relative" data-id="element-3876" data-mp="p6pjzgcl9bfn">
      <style data-id="element-3877" data-mp="1gvan8rha505">{`{pageStyles}`}</style>
      <div className="scanline" data-id="element-3878" data-mp="3sllipjptnuvq"></div>

      {/* Top Navigation Bar (simulated) */}
      <div className="absolute top-0 left-0 w-full p-6 flex justify-between items-center z-10" data-id="element-3879" data-mp="bcyhut16stty">
        <Link to="/" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors" data-id="element-3880" data-mp="6dvfxhgi73q7">
          <ArrowLeft size={20} data-id="element-3881" data-mp="1mr2ey832my38" />
          <span className="font-mono text-sm uppercase tracking-wider" data-id="element-3882" data-mp="1mqakj0l7n6ej">
            Return to Base
          </span>
        </Link>
        <div className="flex gap-6 items-center" data-id="element-3883" data-mp="1jvi6cbywcwp0">
          <span className="text-gray-300 font-bold text-sm tracking-wider" data-id="element-3884" data-mp="1jv8fkm612li9">
            ABOUT THE SALON
          </span>
          <a href="https://thepod.thenetworkingsalon.live/salon" className="bg-red-500 text-white px-4 py-1.5 rounded-full text-sm font-bold tracking-wider hover:bg-red-600 transition-colors" data-id="element-3885" data-mp="1eyrpvnj6jz32">
            PAYMENT
          </a>
        </div>
      </div>

      <div className="w-full max-w-xl mt-12" data-id="element-3886" data-mp="1093okmb010fc">
        <div className="lounge-card p-8 md:p-10 relative overflow-hidden" data-id="element-3887" data-mp="w5nunxqbde6c">
          {/* Header / Step Indicator */}
          <div className="flex justify-between items-center mb-10 border-b border-gray-700 pb-6" data-id="element-3888" data-mp="r6b00qt7b2t7">
            <div className="step-indicator" data-id="element-3889" data-mp="m8stt86kj1ow">
              <span className="border border-red-500/30 p-1 rounded" data-id="element-3890" data-mp="ma431nis91ne">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-3891" data-mp="2ljskg4yb7jk0">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" data-id="element-3892" data-mp="2t2jrqkxh29t8"></path>
                  <polyline points="14 2 14 8 20 8" data-id="element-3893" data-mp="2xw4raheprbuj"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13" data-id="element-3894" data-mp="32tef5smgffg9"></line>
                  <line x1="16" y1="17" x2="8" y2="17" data-id="element-3895" data-mp="256p7b252m8mh"></line>
                  <polyline points="10 9 9 9 8 9" data-id="element-3896" data-mp="2588nytun2los"></polyline>
                </svg>
              </span>
              <span data-id="element-3897" data-mp="2a6aefzue9naa">
                STEP 0{step} / 0{totalSteps}
              </span>
            </div>
            <div className="progress-bar-container" data-id="element-3898" data-mp="2f3k51itis4zp">
              <div className="progress-bar-fill" style={{
              width: `${step / totalSteps * 100}%`
            }} data-id="element-3899" data-mp="2k4hmtuamax0o"></div>
            </div>
          </div>

          <form onSubmit={handleSubmit} data-id="element-3900" data-mp="3vhseqct6yjtx">
            {/* Step 1: Identification */}
            {step === 1 && <div className="animate-fade-in" data-id="element-3901" data-mp="1vfat10lvdi1d">
                <div className="flex items-center gap-3 mb-2" data-id="element-3902" data-mp="1qi119eb0cuad">
                  <User className="text-red-500" size={28} data-id="element-3903" data-mp="1qjvfk9rrxo5h" />
                  <h2 className="text-3xl font-bold text-white" data-id="element-3904" data-mp="24giozc0wy6sa">
                    Identification
                  </h2>
                </div>
                <p className="text-gray-400 font-mono text-sm mb-8" data-id="element-3905" data-mp="20e44yolid0zf">
                  Enter your primary telemetry data.
                </p>

                <div className="space-y-6" data-id="element-3906" data-mp="17s2mkfcpg2xr">
                  <div data-id="element-3907" data-mp="17p6molhar46o">
                    <label className="lounge-label" data-id="element-3908" data-mp="1lio708ht4kxr">FULL_NAME</label>
                    <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} className="lounge-input" placeholder="John Doe" required data-id="element-3909" data-mp="1ljzae77cbqoc" />
                  </div>
                  <div data-id="element-3910" data-mp="ewlctccypgh9">
                    <label className="lounge-label" data-id="element-3911" data-mp="eva7v97s62ok">EMAIL_ADDRESS</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} className="lounge-input" placeholder="john@example.com" required data-id="element-3912" data-mp="jntmm9i7f3uy" />
                  </div>
                  <div data-id="element-3913" data-mp="12mfk7eeiicu4">
                    <label className="lounge-label" data-id="element-3914" data-mp="12mz0yrsfev6k">COMM_LINK (PHONE)</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="lounge-input" placeholder="+234 800 000 0000" required data-id="element-3915" data-mp="np6q4cxtquyp" />
                  </div>
                </div>
              </div>}

            {/* Step 2: Professional Profile */}
            {step === 2 && <div className="animate-fade-in" data-id="element-3916" data-mp="3ouwwyvsdfijh">
                <div className="flex items-center gap-3 mb-2" data-id="element-3917" data-mp="3ou4zfff7raqk">
                  <Briefcase className="text-red-500" size={28} data-id="element-3918" data-mp="3jvahsgszfs81" />
                  <h2 className="text-3xl font-bold text-white" data-id="element-3919" data-mp="3jtposvmbghca">
                    Professional Specs
                  </h2>
                </div>
                <p className="text-gray-400 font-mono text-sm mb-8" data-id="element-3920" data-mp="gfd6g4j34e84">
                  Define your operational parameters.
                </p>

                <div className="space-y-6" data-id="element-3921" data-mp="bdxk895681o5">
                  <div data-id="element-3922" data-mp="6ge413owxetk">
                    <label className="lounge-label" data-id="element-3923" data-mp="6fm9lw71xn4v">TYPE</label>
                    <select name="type" value={formData.type} onChange={handleChange} className="lounge-input appearance-none" required data-id="element-3924" data-mp="1i2tsruph2at">
                      <option value="" disabled data-id="element-3925" data-mp="2ma60l4uslk6u">
                        Select Type...
                      </option>
                      <option value="Individual" data-id="element-3926" data-mp="16p3w32v8lg1n">Individual</option>
                      <option value="Business" data-id="element-3927" data-mp="16pdk4kwq1d4p">Business</option>
                      <option value="Organization" data-id="element-3928" data-mp="1bn5my9ywmjno">Organization</option>
                      <option value="Student" data-id="element-3929" data-mp="1genek7f1f7vz">Student</option>
                    </select>
                  </div>
                  <div data-id="element-3930" data-mp="1lejkwdb7jbep">
                    <label className="lounge-label" data-id="element-3931" data-mp="1ld8f67y7btky">BRAND_NAME</label>
                    <input type="text" name="brandName" value={formData.brandName} onChange={handleChange} className="lounge-input" placeholder="Your brand or company name" required data-id="element-3932" data-mp="1pf29zzlpu3d7" />
                  </div>
                  <div data-id="element-3933" data-mp="4zqiweoclkfn">
                    <label className="lounge-label" data-id="element-3934" data-mp="4yyqtdtym4u4">EXPERTISE</label>
                    <input type="text" name="expertise" value={formData.expertise} onChange={handleChange} className="lounge-input" placeholder="e.g. Marketing, Engineering, Design" required data-id="element-3935" data-mp="3t4gjvmxi5bg7" />
                  </div>
                </div>
              </div>}

            {/* Step 3: Location */}
            {step === 3 && <div className="animate-fade-in" data-id="element-3936" data-mp="bwl0z49jk9m8">
                <div className="flex items-center gap-3 mb-2" data-id="element-3937" data-mp="bvthgrt7y2cb">
                  <MapPin className="text-red-500" size={28} data-id="element-3938" data-mp="pnzmbhqx8mwg" />
                  <h2 className="text-3xl font-bold text-white" data-id="element-3939" data-mp="ppkh98h5e7uu">
                    Location Data
                  </h2>
                </div>
                <p className="text-gray-400 font-mono text-sm mb-8" data-id="element-3940" data-mp="zmyyhaawb0o7">
                  Provide your geographic coordinates.
                </p>

                <div className="space-y-6" data-id="element-3941" data-mp="ukq4camadski">
                  <div data-id="element-3942" data-mp="1e8crv5o8bu0b">
                    <label className="lounge-label" data-id="element-3943" data-mp="1e9xkgpqzh2vj">CITY_ID</label>
                    <input type="text" name="cityId" value={formData.cityId} onChange={handleChange} className="lounge-input" placeholder="e.g. LOS, ABJ, PHC" required data-id="element-3944" data-mp="1iapwtrv5jnw1" />
                  </div>
                  <div data-id="element-3945" data-mp="uisk7yjvortv">
                    <label className="lounge-label" data-id="element-3946" data-mp="zbvi2ivounfv">CITY_NAME</label>
                    <input type="text" name="cityName" value={formData.cityName} onChange={handleChange} className="lounge-input" placeholder="e.g. Lagos, Abuja, Port Harcourt" required data-id="element-3947" data-mp="zd6lghl81t6g" />
                  </div>
                </div>
              </div>}

            {/* Step 4: Confirmation */}
            {step === 4 && <div className="animate-fade-in" data-id="element-3948" data-mp="s1ac5q98rlbo">
                <div className="flex items-center gap-3 mb-2" data-id="element-3949" data-mp="s2lh3tefaz4d">
                  <CheckCircle className="text-red-500" size={28} data-id="element-3950" data-mp="n1xfyewhd1wf" />
                  <h2 className="text-3xl font-bold text-white" data-id="element-3951" data-mp="n4a5pe8hcgk1">
                    Verification
                  </h2>
                </div>
                <p className="text-gray-400 font-mono text-sm mb-8" data-id="element-3952" data-mp="d4j7b1gstheo">
                  Review your telemetry data before transmission.
                </p>

                <div className="space-y-4 bg-gray-900/50 p-6 rounded-lg border border-gray-800" data-id="element-3953" data-mp="8b6nr5qz80ss">
                  <div className="grid grid-cols-3 gap-4 border-b border-gray-800 pb-3" data-id="element-3954" data-mp="49lfxytaucpg">
                    <span className="lounge-label mb-0 col-span-1" data-id="element-3955" data-mp="3vjtv22615ett">NAME:</span>
                    <span className="text-gray-300 font-mono text-sm col-span-2" data-id="element-3956" data-mp="3qhl1p4p8war6">
                      {formData.fullName || 'N/A'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 border-b border-gray-800 pb-3" data-id="element-3957" data-mp="3beuopv6hdiz4">
                    <span className="lounge-label mb-0 col-span-1" data-id="element-3958" data-mp="3ge69df9yx8lz">EMAIL:</span>
                    <span className="text-gray-300 font-mono text-sm col-span-2" data-id="element-3959" data-mp="3lebtmadwrsu1">
                      {formData.email || 'N/A'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 border-b border-gray-800 pb-3" data-id="element-3960" data-mp="3qastu2ydtxnh">
                    <span className="lounge-label mb-0 col-span-1" data-id="element-3961" data-mp="2snaajy73hd58">PHONE:</span>
                    <span className="text-gray-300 font-mono text-sm col-span-2" data-id="element-3962" data-mp="2xozkfaikzkrq">
                      {formData.phone || 'N/A'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 border-b border-gray-800 pb-3" data-id="element-3963" data-mp="37fcebtcacqaj">
                    <span className="lounge-label mb-0 col-span-1" data-id="element-3964" data-mp="29zosmij7l0pq">TYPE:</span>
                    <span className="text-gray-300 font-mono text-sm col-span-2" data-id="element-3965" data-mp="2exqd97wa983b">
                      {formData.type || 'N/A'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 border-b border-gray-800 pb-3" data-id="element-3966" data-mp="b3ayx9pisuq8">
                    <span className="lounge-label mb-0 col-span-1" data-id="element-3967" data-mp="g24dl5gjmrrz">BRAND:</span>
                    <span className="text-gray-300 font-mono text-sm col-span-2" data-id="element-3968" data-mp="kzmjemz9kqvr">
                      {formData.brandName || 'N/A'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 border-b border-gray-800 pb-3" data-id="element-3969" data-mp="3oh4ledscv3o9">
                    <span className="lounge-label mb-0 col-span-1" data-id="element-3970" data-mp="3tfzdy7g4k6lk">
                      EXPERTISE:
                    </span>
                    <span className="text-gray-300 font-mono text-sm col-span-2" data-id="element-3971" data-mp="19u9ehwf21sd">
                      {formData.expertise || 'N/A'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 border-b border-gray-800 pb-3" data-id="element-3972" data-mp="35ok5ry1o2v2e">
                    <span className="lounge-label mb-0 col-span-1" data-id="element-3973" data-mp="39pmauvi9m1ca">CITY:</span>
                    <span className="text-gray-300 font-mono text-sm col-span-2" data-id="element-3974" data-mp="327o4gzvdr6da">
                      {formData.cityName || 'N/A'} ({formData.cityId || 'N/A'})
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 pt-1" data-id="element-3975" data-mp="2t5omo5cjjy9a">
                    <span className="lounge-label mb-0 col-span-1" data-id="element-3976" data-mp="2o7n19drn7mun">
                      STATUS:
                    </span>
                    <span className="text-green-500 font-mono text-sm col-span-2 flex items-center gap-2" data-id="element-3977" data-mp="2j8tnrlccvvuh">
                      <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" data-id="element-3978" data-mp="2egsfeaib6p9q"></span>
                      READY FOR UPLOAD
                    </span>
                  </div>
                </div>
              </div>}

            {/* Navigation Buttons */}
            <div className="mt-10 pt-6 border-t border-gray-800 flex gap-4" data-id="element-3979" data-mp="20ilq3irfdxlo">
              {step > 1 && <button type="button" onClick={prevStep} className="px-6 py-3 rounded border border-gray-700 text-gray-400 font-mono text-sm hover:bg-gray-800 hover:text-white transition-colors" disabled={isSubmitting} data-id="element-3980" data-mp="1viplbhawm252">
                  &lt; BACK
                </button>}
              <button type="submit" disabled={isSubmitting} className={`lounge-btn flex-1 flex justify-center items-center gap-2 ${step === 1 && formData.fullName && formData.email && formData.phone || step === 2 && formData.type && formData.brandName && formData.expertise || step === 3 && formData.cityId && formData.cityName || step === 4 ? 'active' : ''} ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-3981" data-mp="1gwac745lzy4w">
                {isSubmitting ? <>
                    <Loader2 size={18} className="animate-spin" data-id="element-3982" data-mp="2njxky424wwd1" />
                    TRANSMITTING...
                  </> : step === totalSteps ? 'INITIALIZE SEQUENCE' : <>
                    CONTINUE
                    <ChevronRight size={18} data-id="element-3983" data-mp="by5engh6ohny" />
                  </>}
              </button>
            </div>
            {submitError && <p className="mt-4 text-red-500 font-mono text-sm text-center" data-id="element-3984" data-mp="qo2xri09ah6a">
                {submitError}
              </p>}
          </form>
        </div>
      </div>
    </div>;
};